import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Typography, IconButton, Tooltip, TableSortLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import moment from 'moment';
import LoadProgress from '../../common/LoadProgress.js';
import SystemAlert from '../../common/SystemAlert.js';
import { API_URL } from '../../common/Constants.js';
import Cookies from '../../common/Cookies.js';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';

export default function Agents() {
  const [kbModalOpen, setKbModalOpen] = React.useState(false);
  const [checkingKbs, setCheckingKbs] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [agents, setAgents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [alert, setAlert] = React.useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');
  const [agentToDelete, setAgentToDelete] = React.useState(null);
  const [planAgentLimit, setPlanAgentLimit] = React.useState(null);
  const [currentPlanName, setCurrentPlanName] = React.useState(null);
  const [nextPlanName, setNextPlanName] = React.useState(null);
  // Sorting state
  const [sortBy, setSortBy] = React.useState('dateUpdated');
  const [sortDirection, setSortDirection] = React.useState('desc');

  const navigate = useNavigate();

  const isUnlimitedAgentsPlan = planAgentLimit != null && planAgentLimit >= 999;
  const isAgentLimitReached = planAgentLimit != null && !isUnlimitedAgentsPlan && agents.length >= planAgentLimit;

  const getAgentLimitMessage = () => {
    const planLabel = currentPlanName || 'current';
    if (nextPlanName) {
      return `You've reached the ${planLabel} limit of ${planAgentLimit} chatbot agents. Upgrade to ${nextPlanName} to create more agents.`;
    }
    return `You've reached the ${planLabel} limit of ${planAgentLimit} chatbot agents.`;
  };

  const handleAddAgentAdvanced = async () => {
    if (isAgentLimitReached) {
      setAlert({ severity: 'warning', message: getAgentLimitMessage() });
      return;
    }
    console.log("Add New Agent (Advanced) button clicked");
    setCheckingKbs(true);
    try {
      const jwtToken = Cookies.getJwtToken();
      console.log("JWT Token:", jwtToken);
      console.log("API_URL:", API_URL);
      const url = `${API_URL}/kbs/hasAnyKbs`;
      console.log("About to call axios.post to:", url);
      const response = await axios.post(url, null, {
        headers: {
          Authorization: `Bearer ${jwtToken}`
        }
      });
      console.log("axios.post response:", response);
      if (response.data && response.data.hasKbs) {
        console.log("hasKbs is true, navigating to /chat-main?newAgent=true");
        navigate('/chat-main?newAgent=true');
      } else {
        console.log("hasKbs is false or missing, opening KB modal");
        setKbModalOpen(true);
      }
    } catch (err) {
      console.log("Error in handleAddAgentAdvanced:", err);
      setAlert({ severity: 'error', message: 'Failed to check Knowledge Bases. Please try again.' });
    } finally {
      setCheckingKbs(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedAgents = React.useMemo(() => {
    const sorted = [...agents].sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'agentName') {
        aValue = a.agentName?.toLowerCase() || '';
        bValue = b.agentName?.toLowerCase() || '';
      } else if (sortBy === 'dateCreated') {
        aValue = a.dateCreated || '';
        bValue = b.dateCreated || '';
      } else if (sortBy === 'dateUpdated') {
        aValue = a.dateUpdated || '';
        bValue = b.dateUpdated || '';
      } else {
        aValue = '';
        bValue = '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [agents, sortBy, sortDirection]);

  const handleDeleteClick = (id) => {
    const agent = agents.find(agent => agent.id === id);
    setAgentToDelete(agent);
    setDeleteId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.post('/agents/deleteAgent', null, { params: { agentId: deleteId } });
      // Remove the deleted agent from the state
      setAgents(agents.filter(agent => agent.id !== deleteId));
      setOpenDialog(false);
      setAlert({ severity: 'success', message: 'Agent deleted successfully' });
    } catch (err) {
      console.error('Error deleting agent:', err);
      setAlert({ severity: 'error', message: 'Failed to delete agent' });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteConfirmText('');
  };

  const handleDuplicate = async (id) => {
    if (isAgentLimitReached) {
      setAlert({ severity: 'warning', message: getAgentLimitMessage() });
      return;
    }
    try {
      const response = await axios.post('/agents/duplicate', null, { params: { id } });
      // Add the duplicated agent to the state
      setAgents([...agents, response.data]);
      setAlert({ severity: 'success', message: 'Agent duplicated successfully' });
    } catch (err) {
      console.error('Error duplicating agent:', err);
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
      setAlert({ severity: 'error', message: apiMessage || 'Failed to duplicate agent' });
    }
  };

  React.useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        const response = await axios.post('/agents/getAllAgents');
        console.log(response.data);
        setAgents(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching agents:', err);
        setError('Failed to load agents. Please try again later.');
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  React.useEffect(() => {
    const fetchAgentPlanLimit = async () => {
      try {
        const jwtToken = Cookies.getJwtToken();
        const headers = {
          Authorization: `Bearer ${jwtToken}`
        };

        const [planResponse, plansResponse] = await Promise.all([
          axios.get(`${API_URL}/account/plan`, { headers }),
          axios.get(`${API_URL}/account/plans`, { headers })
        ]);

        const tenantPlanCode = (planResponse?.data?.plan || '').toString().toUpperCase();
        const plans = plansResponse?.data?.plans || [];
        const currentIndex = plans.findIndex(plan => (plan.value || '').toString().toUpperCase() === tenantPlanCode);
        const currentPlan = currentIndex >= 0 ? plans[currentIndex] : null;
        const nextPlan = currentIndex >= 0 && currentIndex < plans.length - 1 ? plans[currentIndex + 1] : null;
        const aiAgentsLimit = currentPlan != null ? Number(currentPlan.aiAgents) : null;

        if (currentPlan && Number.isFinite(aiAgentsLimit)) {
          setPlanAgentLimit(aiAgentsLimit);
          setCurrentPlanName(currentPlan.name || null);
          setNextPlanName(nextPlan?.name || null);
        }
      } catch (err) {
        console.error('Error fetching agent plan limit:', err);
      }
    };

    fetchAgentPlanLimit();
  }, []);

  return (
    <div>
      {
        alert && (
          <SystemAlert open={alert !== null} severity={alert.severity} message={alert.message} />
        )
      }

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px' }}>
        <Typography variant="h5" component="h1">
          Agents
        </Typography>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Button
            variant='outlined'
            onClick={handleAddAgentAdvanced}
            disabled={checkingKbs || isAgentLimitReached}
          >
            {checkingKbs ? 'Checking...' : (isAgentLimitReached ? 'Agent Limit Reached' : 'Add New Agent')}
          </Button>
          {isAgentLimitReached && (
            <Button
              variant='contained'
              color='primary'
              onClick={() => navigate('/AccountSettings?tab=plan-information')}
            >
              {nextPlanName ? `Upgrade to ${nextPlanName}` : 'View Plans'}
            </Button>
          )}
          {/* Modal for no KBs */}
          <Dialog
            open={kbModalOpen}
            onClose={() => setKbModalOpen(false)}
            aria-labelledby="kb-required-dialog-title"
            aria-describedby="kb-required-dialog-description"
          >
            <DialogTitle id="kb-required-dialog-title">
              Knowledge Base Required
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="kb-required-dialog-description">
                To create an Agent using the Advanced Mode requires at least one KnowledgeBase. Would you like to create a Knowledge Base?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setKbModalOpen(false); navigate('/agents'); }}>Cancel</Button>
              <Button variant="contained" onClick={() => { setKbModalOpen(false); navigate('/KnowledgeInfo'); }}>Create Knowledge Base</Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <LoadProgress open={loading}></LoadProgress>
      ) : error ? (
        <div style={{ color: 'error.main', textAlign: 'center', padding: '20px' }}>
          {error}
        </div>
      ) : (
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'agentName'}
                  direction={sortBy === 'agentName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('agentName')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'dateCreated'}
                  direction={sortBy === 'dateCreated' ? sortDirection : 'asc'}
                  onClick={() => handleSort('dateCreated')}
                >
                  Created On
                </TableSortLabel>
              </TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'dateUpdated'}
                  direction={sortBy === 'dateUpdated' ? sortDirection : 'asc'}
                  onClick={() => handleSort('dateUpdated')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAgents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No agents found</TableCell>
              </TableRow>
            ) : (
              sortedAgents.map((agent) => (
                <TableRow
                  key={agent.id}
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  <TableCell component="th" scope="row">
                    {agent.agentName}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {agent.active ? 'Active' : 'Inactive'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {agent.createdBy}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {moment(agent.dateCreated).format('MMMM Do, YYYY')}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {agent.updatedBy}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {moment(agent.dateUpdated).format('MMMM Do, YYYY')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        sx={{ cursor: 'pointer', color: 'success.main' }}
                        onClick={() => navigate(`/Agent/${agent.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        disabled={isAgentLimitReached}
                        onClick={() => handleDuplicate(agent.id)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ cursor: 'pointer', color: 'error.main' }}
                        onClick={() => handleDeleteClick(agent.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: 700, maxWidth: '100%' }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this agent:
            <Box component="div" mt={1} mb={2} sx={{ pl: 2, fontWeight: 'bold' }}>
              {agentToDelete ? agentToDelete.agentName : ''}
            </Box>
            <Box component="div" mb={2}>
              This action cannot be undone.
            </Box>
            <Box component="div" mb={1}>
              Type <span style={{ color: 'red', fontWeight: 'bold' }}>Delete</span> (case sensitive) to confirm.
            </Box>
          </DialogContentText>
          <Box mt={2}>
            <TextField
              autoFocus
              size="small"
              label='Type "Delete" to confirm'
              fullWidth
              variant="outlined"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            variant='contained'
            disabled={deleteConfirmText !== 'Delete'}
          >
            Delete
          </Button>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}